import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { PageHeader } from '../components/PageHeader';
import { Footer } from '../components/Footer';
import { ShieldCheckIcon, ClockIcon, CreditCardIcon, ParkingCircleIcon, LampIcon, PhoneIcon } from 'lucide-react';
const features = [{
  icon: ParkingCircleIcon,
  title: 'Truck2Park Reservierung',
  desc: 'Reservieren Sie Ihren LKW-Stellplatz bequem vorab über das Truck2Park-System.'
}, {
  icon: ShieldCheckIcon,
  title: 'Überwachtes Gelände',
  desc: '24/7 Videoüberwachung und regelmäßige Kontrollgänge für maximale Sicherheit.'
}, {
  icon: LampIcon,
  title: 'Beleuchtete Stellplätze',
  desc: 'Alle Parkplätze sind vollständig beleuchtet – auch nachts sicher unterwegs.'
}, {
  icon: CreditCardIcon,
  title: 'Flexible Bezahlung',
  desc: 'Akzeptiert werden DKV, UTA und alle gängigen Tankkarten.'
}];
const stagger = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
export function TruckParkingPage() {
  return <>
      <PageHeader title="LKW Parken & Truck2Park" subtitle="100+ Stellplätze. Sicher. Überwacht. 24/7." image="https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=1200&q=80" accentColor="text-transparent bg-clip-text bg-gradient-to-r from-white to-teal-accent" accentGlow="bg-teal-accent" data-id="element-506" />

      <section className="py-20 px-4 bg-base" data-id="element-507">
        <div className="max-w-7xl mx-auto" data-id="element-508">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-w-3xl mb-16" data-id="element-509">
            <h2 className="text-4xl font-heading font-bold mb-6 text-white" data-id="element-510">
              Optimale Bedingungen für Berufskraftfahrer
            </h2>
            <p className="text-lg text-white/60 leading-relaxed" data-id="element-511">
              Unser Rastpark bietet über 100 großzügige LKW-Stellplätze mit
              modernster Infrastruktur. Reservierbare und überwachte Parkplätze
              garantieren eine ruhige und sichere Nacht. Perfekt angebunden an
              die A9, Ausfahrt 39.
            </p>
          </motion.div>

          <div className="flex items-center gap-8 mb-16" data-id="element-512">
            <div className="flex items-center gap-4" data-id="element-513">
              <img src="https://cdn.magicpatterns.com/uploads/67bAeTyNrjdaEY6ordpJVd/DKV-Logo.svg.png" alt="DKV" className="h-14 rounded-lg object-contain" data-id="element-514" />
              <img src="https://cdn.magicpatterns.com/uploads/hboVHWhntwKeAH3qde42ij/Union_Tank_Eckstein_logo.svg.png" alt="UTA" className="h-14 object-contain" data-id="element-515" />
            </div>
            <span className="text-white/40 text-lg" data-id="element-516">Akzeptiert</span>
          </div>

          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{
          once: true
        }} className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-20" data-id="element-517">
            {features.map(f => {
            const Icon = f.icon;
            return <motion.div key={f.title} variants={fadeUp} className="glass-card p-8 rounded-2xl border-teal-accent/10" data-id="element-518">
                  <div className="w-12 h-12 rounded-xl bg-teal-accent/10 flex items-center justify-center mb-5" data-id="element-519">
                    <Icon className="w-6 h-6 text-teal-accent" strokeWidth={1.5} data-id="element-520" />
                  </div>
                  <h3 className="text-xl font-heading font-bold mb-2 text-white" data-id="element-521">
                    {f.title}
                  </h3>
                  <p className="text-white/60" data-id="element-522">{f.desc}</p>
                </motion.div>;
          })}
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-20" data-id="element-523">
            <div className="glass-card p-8 rounded-2xl text-center border-teal-accent/10" data-id="element-524">
              <div className="text-4xl font-heading font-bold text-teal-accent mb-2" data-id="element-525">
                100+
              </div>
              <div className="text-white/60" data-id="element-526">LKW Stellplätze</div>
            </div>
            <div className="glass-card p-8 rounded-2xl text-center border-teal-accent/10" data-id="element-527">
              <div className="text-4xl font-heading font-bold text-teal-accent mb-2" data-id="element-528">
                24/7
              </div>
              <div className="text-white/60" data-id="element-529">Geöffnet</div>
            </div>
            <div className="glass-card p-8 rounded-2xl text-center border-teal-accent/10" data-id="element-530">
              <div className="text-4xl font-heading font-bold text-teal-accent mb-2" data-id="element-531">
                A9
              </div>
              <div className="text-white/60" data-id="element-532">Ausfahrt 39</div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="glass-card p-8 rounded-2xl border-teal-accent/20 max-w-xl" data-id="element-533">
            <h3 className="text-xl font-heading font-bold text-white mb-4" data-id="element-534">
              Stellplatz reservieren
            </h3>
            <p className="text-white/60 mb-6" data-id="element-535">
              Reservieren Sie über Truck2Park oder kontaktieren Sie uns direkt.
            </p>
            <div className="flex items-center gap-3" data-id="element-536">
              <PhoneIcon className="w-5 h-5 text-teal-accent" data-id="element-537" />
              <a href="tel:+499273501730" className="text-white hover:text-teal-accent transition-colors text-lg font-medium" data-id="element-538">
                09273 / 50173-0
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-539" />
    </>;
}